import router from '@/router'
import { useUserStoreHook } from '@/store/modules/user'
import { usePermissionStoreHook } from '@/store/modules/permission'
import { translateRouteTitle, getPageTitle } from '@/utils/index'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

// 进度条
NProgress.configure({ showSpinner: false })

const userStore = useUserStoreHook()
const permissionStore = usePermissionStoreHook()

// 白名单路由
const whiteList = ['/login', '/setting/help-document']

/**
 * 检查是否有首页路由权限
 * 根据接口 admin/system.UserCenter/info 返回的 menus 数据判断
 * @param {Array} routes 路由列表（可选）
 * @returns {boolean} 是否有首页权限
 */
function hasHomeRoutePermission(routes = null) {
  // 从接口返回的 menus 数据中检查是否有首页菜单
  const userMenus = userStore.user.menus || []
  
  // 递归检查菜单（支持树形结构和扁平结构）
  function checkMenu(menuList) {
    if (!Array.isArray(menuList) || menuList.length === 0) return false
    
    for (const menu of menuList) {
      // 检查菜单名称是否为"首页"
      if (menu.menu_name === '首页') {
        return true
      }
      // 检查是否是首页类型的菜单（menu_type 为 2 且 menu_pid 为 0）
      if (menu.menu_type === 2 && menu.menu_pid === 0) {
        return true
      }
      // 检查路径是否为首页相关路径
      if (menu.path === '/' || menu.path === '/dashboard' || menu.path === '/index' || 
          menu.path === 'dashboard' || menu.path === 'index') {
        return true
      }
      // 递归检查子菜单
      if (menu.children && menu.children.length > 0) {
        if (checkMenu(menu.children)) {
          return true
        }
      }
    }
    return false
  }
  
  // 检查用户菜单中是否有首页
  const hasHomeMenu = checkMenu(userMenus)
  if (hasHomeMenu) {
    return true
  }
  
  // 如果用户菜单中没有首页，再检查路由列表（作为备用）
  if (routes && routes.length > 0) {
    for (const route of routes) {
      if (route.path === '/') {
        // 如果路由有 roles 配置，说明是动态路由，检查权限
        if (route.meta?.roles && route.meta.roles.length > 0) {
          const { hasPerm } = require('@/utils/index')
          return hasPerm(route.meta.roles)
        }
      }
      if (route.children && route.children.length > 0) {
        const hasHomeChild = route.children.some((child) => {
          if (child.path === 'dashboard' || child.path === 'index' || 
              child.path === '/dashboard' || child.path === '/index') {
            if (child.meta?.roles && child.meta.roles.length > 0) {
              const { hasPerm } = require('@/utils/index')
              return hasPerm(child.meta.roles)
            }
            return false
          }
          return false
        })
        if (hasHomeChild) {
          return true
        }
      }
    }
  }
  
  // 默认返回 false，表示没有首页权限
  return false
}

/**
 * 查找第一个可访问的菜单路由
 * @param {Array} routes 路由列表
 * @returns {string|null} 第一个菜单路由路径
 */
function findFirstMenuRoute(routes) {
  for (const route of routes) {
    // 跳过隐藏的路由、登录页、重定向页和首页
    if (
      route.meta?.hidden ||
      route.path === '/login' ||
      route.path === '/redirect' ||
      route.path === '/' ||
      route.path === '/dashboard'
    ) {
      continue
    }
    // 如果有子路由，递归查找第一个子路由
    if (route.children && route.children.length > 0) {
      const childRoute = findFirstMenuRoute(route.children)
      if (childRoute) {
        // 拼接完整路径
        const basePath = route.path === '/' ? '' : route.path
        return basePath + (childRoute.startsWith('/') ? childRoute : '/' + childRoute)
      }
    }
    // 如果是菜单路由（有 path 且不是 Layout）
    if (route.path && route.component) {
      const componentStr = route.component.toString()
      // 如果不是 Layout 组件，返回路径
      if (!componentStr.includes('Layout') && !componentStr.includes('layout/index')) {
        return route.path
      }
    }
  }
  return null
}

router.beforeEach(async (to, from, next) => {
  NProgress.start()
  // 设置页面标题
  document.title = getPageTitle(translateRouteTitle(to.meta.title))
  // 获取token
  const hasToken = userStore.getToken()
  if (hasToken) {
    if (to.path === '/login') {
      // 如果已登录，检查是否有首页权限，如果没有则跳转到第一个有权限的菜单
      const hasRoles = userStore.user.roles && userStore.user.roles.length > 0
      if (hasRoles) {
        const routes = permissionStore.routes
        if (hasHomeRoutePermission(routes)) {
          next({ path: '/' })
        } else {
          const firstMenuRoute = findFirstMenuRoute(routes)
          if (firstMenuRoute) {
            next({ path: firstMenuRoute })
          } else {
            next({ path: '/' })
          }
        }
      } else {
        // 如果还没有角色信息，先跳转首页，后续在获取用户信息后会检查权限
        next({ path: '/' })
      }
      NProgress.done()
    } else {
      const hasRoles = userStore.user.roles && userStore.user.roles.length > 0
      if (hasRoles) {
        // 如果访问首页，检查是否有首页权限
        if (to.path === '/' || to.path === '/dashboard' || to.path === '/index') {
          const routes = permissionStore.routes
          // 如果没有首页权限，跳转到第一个有权限的菜单
          if (!hasHomeRoutePermission(routes)) {
            const firstMenuRoute = findFirstMenuRoute(routes)
            if (firstMenuRoute) {
              next({ path: firstMenuRoute, replace: true })
              NProgress.done()
              return
            }
          }
        }
        
        // 未匹配到任何路由，跳转404
        if (to.matched.length === 0) {
          from.name ? next({ name: from.name }) : next('/404')
        } else {
          next()
        }
      } else {
        try {
          const { menus } = await userStore.userInfo()
          const accessRoutes = await permissionStore.generateRoutes(menus)
          accessRoutes.forEach((route) => {
            router.addRoute(route)
          })
          
          // 如果访问首页，检查是否有首页权限
          if (to.path === '/' || to.path === '/dashboard' || to.path === '/index') {
            // 检查用户菜单中是否有首页
            if (!hasHomeRoutePermission(accessRoutes)) {
              const firstMenuRoute = findFirstMenuRoute(accessRoutes)
              if (firstMenuRoute) {
                next({ path: firstMenuRoute, replace: true })
                NProgress.done()
                return
              }
            }
          }
          
          next({ ...to, replace: true })
        } catch (error) {
          // 移除 token 并跳转登录页
          await userStore.resetToken()
          next(`/login?redirect=${to.path}`)
          NProgress.done()
        }
      }
    }
  } else {
    // 未登录可以访问白名单页面
    if (whiteList.indexOf(to.path) !== -1) {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
      NProgress.done()
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})
